import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
  Avatar,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@material-ui/core';

import {
  getCurrencyDisplay,
  getInitials,
  getTimeFromXDisplay
} from '../../../../helpers';
import { buildJobUrl } from '../../../../helpers/urlHelpers';
import ExtraInfoChips from '../../../../components/ExtraInfoChips/ExtraInfoChips';
import { useHistory } from 'react-router-dom';
import { CATEGORIES_JOB, PLAN_TYPE, TYPES_JOB } from '../../../../config';

const fontSizeMobileList = '12px';
const textWidthMobileList = '215px';
const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: 0
  },
  jobRow: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
    position: 'relative', // Added for absolute positioned children
    width: '100%',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
      borderRadius: theme.spacing(1),
      width: '86%',
      minHeight: '67px'
    }
  },
  jobAvatar: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  jobDetails: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column'
  },
  avatar: {
    marginRight: theme.spacing(3),
    width: '50px',
    height: '50px',
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      width: '40px',
      height: '40px',
      marginRight: theme.spacing(1.5)
    }
  },
  actions: {
    justifyContent: 'flex-end'
  },
  jobTitle: {
    fontWeight: 'bold',
    fontSize: '1.2rem',
    marginTop: '8px',
    marginBottom: '8px',
    width: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    [theme.breakpoints.down('md')]: {
      fontSize: '14px',
      marginTop: '4px',
      marginBottom: '4px',
      lineHeight: '1.3',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%'
    }
  },
  jobCompanyName: {
    fontSize: '1rem',
    width: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    [theme.breakpoints.down('md')]: {
      fontSize: '11px',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%'
    }
  },
  jobShortSubTitle: {
    fontSize: '1rem',
    [theme.breakpoints.down('md')]: {
      fontSize: fontSizeMobileList,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width: textWidthMobileList
    }
  },
  timeAgoLabel: {
    flexShrink: 0,
    fontSize: '1rem',
    textAlign: 'right',
    minWidth: '80px',
    [theme.breakpoints.down('md')]: {
      fontSize: '10px',
      minWidth: '45px'
    }
  },
  previewRecords: {
    opacity: 0.2
  },
  normalRecords: {
    opacity: '1 !important'
  },
  previewRecord0: {
    opacity: 0.2
  },
  previewRecord1: {
    opacity: 0.3
  },
  previewRecord2: {
    opacity: 0.4
  },
  rowRecordInfoTable: {},
  highlightWithYellowShadow: {
    border: '2px solid rgba(255, 205, 0, 0.55)',
    borderRadius: '12px !important',
    backgroundImage:
      'linear-gradient(90deg, rgba(255, 214, 102, 0.18) 0%, rgba(255, 255, 255, 0) 60%)',
    boxShadow: '0 12px 28px -26px rgba(255, 190, 50, 0.6)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: 'linear-gradient(90deg, #FFD26F, rgba(255,255,255,0))',
      opacity: 0.8
    }
  },
  highlightWithBrandColor: {
    borderLeft: '6px solid var(--brandColor)',
    borderTop: '1px solid var(--brandColor)',
    borderBottom: '1px solid var(--brandColor)',
    borderRight: '1px solid var(--brandColor)',
    borderRadius: '14px !important',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      background:
        'linear-gradient(180deg, transparent, var(--brandColor) 35%, var(--brandColor) 65%, transparent)',
      opacity: 0.75
    }
  },
  // Sticky indicator chip style
  stickyChipsWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(0.25),
      marginTop: theme.spacing(0.5)
    }
  },
  stickyChip: {
    marginRight: theme.spacing(1),
    height: 28,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 196, 76, 0.15)',
    border: '1px solid rgba(255, 196, 76, 0.45)',
    color: '#8a5b00',
    fontWeight: 600,
    letterSpacing: '0.02em',
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(1.2)
    }
  },
  stickyChipIcon: {
    marginLeft: theme.spacing(1)
  },
  verifiedChip: {
    marginRight: theme.spacing(1)
  },
  verifiedChipIcon: {},
  hideOnMobile: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  }
}));

const JobTable = ({ jobResults, jobToPreviewResult, isPreview }) => {
  const classes = useStyles();
  const isJobPreview = (job, jobPreview) => {
    const match = job?.id === jobPreview?.job?.id;
    if (isPreview && match) {
      console.log('🎯 MATCHED PREVIEW JOB:', job?.title, 'ID:', job?.id);
    }
    return match;
  };
  const getPreviewRecordStyle = (position) => {
    const previewRecordStyle = [
      classes.previewRecord0,
      classes.previewRecord1,
      classes.previewRecord2,
      classes.previewRecord2,
      classes.previewRecord2,
      classes.previewRecord1,
      classes.previewRecord0
    ];
    return previewRecordStyle[position];
  };

  const hexToRgba = (hex, alpha) => {
    if (!hex || typeof hex !== 'string') return `rgba(0,0,0,${alpha})`;
    const normalized = hex.replace('#', '');
    const isShort = normalized.length === 3;
    const fullHex = isShort
      ? normalized
          .split('')
          .map((ch) => ch + ch)
          .join('')
      : normalized;
    const intValue = Number.parseInt(fullHex, 16);
    if (Number.isNaN(intValue)) {
      return `rgba(0,0,0,${alpha})`;
    }
    const r = (intValue >> 16) & 255;
    const g = (intValue >> 8) & 255;
    const b = intValue & 255;
    return `rgba(${r},${g},${b},${alpha})`;
  };

  // Helper function to normalize plan to array format
  const normalizePlan = (plan) => {
    if (!plan) return [];
    if (Array.isArray(plan)) return plan;
    return [plan]; // Convert single object to array
  };

  // Helper function to check if yellow highlight is enabled
  const hasYellowHighlight = (jobResult) => {
    if (!jobResult) return false;

    // Check job.activeBenefits (respects expiration from backend)
    if (jobResult.job?.activeBenefits?.HIGHLIGHT_YELLOW === true) {
      console.log(`⚠️ Job "${jobResult.job?.title}" has YELLOW highlight`);
      return true;
    }

    return false;
  };

  // Helper function to check if brand color highlight is enabled
  const hasBrandColorHighlight = (jobResult) => {
    if (!jobResult) return false;

    // Check job.activeBenefits (respects expiration from backend)
    if (jobResult.job?.activeBenefits?.HIGHLIGHT_BRAND === true) {
      console.log(`🎨 Job "${jobResult.job?.title}" has BRAND COLOR highlight`);
      return true;
    }

    return false;
  };

  // Helper function to get brand color from active benefit metadata
  const getBrandColorFromBenefit = (jobResult) => {
    const color =
      jobResult?.job?.activeBenefitMetadata?.HIGHLIGHT_BRAND?.brandColor;
    if (color) {
      console.log(`🎨 Job "${jobResult.job?.title}" brand color: ${color}`);
      return color;
    }
    return null;
  };

  // Helper function to check if logo should be shown
  const shouldShowLogo = (jobResult) => {
    // We always show logo for now since does not look good and the app does not depend on it as main feature
    return true;
  };

  // Helper function to check if job has an active sticky plan
  const hasStickyPlan = (jobResult) => {
    if (!jobResult) return false;

    // Check job.activeBenefits (respects expiration from backend)
    if (jobResult.job?.activeBenefits) {
      const benefits = jobResult.job.activeBenefits;
      if (
        benefits.STICKY_24H === true ||
        benefits.STICKY_1W === true ||
        benefits.STICKY_1M === true
      ) {
        console.log(`📌 Job "${jobResult.job?.title}" has STICKY plan`);
        return true;
      }
    }

    return false;
  };

  // Helper function to check if geo-lock is enabled
  const hasGeoLock = (jobResult) => {
    if (!jobResult || !jobResult.plan) return false;

    const plans = normalizePlan(jobResult.plan);

    // Check if any plan has geo-lock
    for (const plan of plans) {
      // Check items for geo-lock plan type
      if (plan.items) {
        const planItems = Array.isArray(plan.items) ? plan.items : [plan.items];
        const hasGeoLockPlan = planItems.some((item) => {
          return (
            typeof item === 'object' &&
            (item.planId === PLAN_TYPE.GEO_LOCK ||
              item.id === PLAN_TYPE.GEO_LOCK)
          );
        });
        if (hasGeoLockPlan) {
          return true;
        }
      }

      // Also check metadata
      if (plan.metadata && plan.metadata.geoLockEnabled === true) {
        return true;
      }
    }

    return false;
  };

  // Helper function to get sticky plan remaining time
  const getStickyDuration = (jobResult) => {
    if (!jobResult || !jobResult.plan) {
      return null;
    }

    const plans = normalizePlan(jobResult.plan);
    const STICKY_PLAN_IDS = [5, 6, 7]; // 24 hours, 1 week, 1 month
    const STICKY_PLAN_LABELS = {
      5: '24h',
      6: '1 week',
      7: '1 month'
    };

    for (const plan of plans) {
      if (!plan.isActive) continue;

      // Skip expired plans
      if (plan.expiresAt && new Date(plan.expiresAt) < new Date()) continue;

      // Check if plan has sticky feature
      if (plan.metadata && plan.metadata.selectedPlans) {
        const selectedPlans = Array.isArray(plan.metadata.selectedPlans)
          ? plan.metadata.selectedPlans
          : [];

        // Find the sticky plan
        const stickyPlan = selectedPlans.find((sp) =>
          STICKY_PLAN_IDS.includes(sp.id)
        );

        if (stickyPlan) {
          // If expiresAt exists, calculate remaining time
          if (plan.expiresAt) {
            const now = new Date();
            const expiresAt = new Date(plan.expiresAt);
            const diffMs = expiresAt - now;

            console.log('📌 [Sticky Duration] Job:', jobResult.job?.title);
            console.log('   expiresAt (raw):', plan.expiresAt);
            console.log('   expiresAt (parsed):', expiresAt);
            console.log('   now:', now);
            console.log('   diffMs:', diffMs);

            if (diffMs <= 0) {
              console.log('   ⚠️ Plan expired, skipping');
              continue; // Skip expired plans
            }

            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            const diffDays = Math.floor(diffHours / 24);

            console.log('   diffDays:', diffDays, 'diffHours:', diffHours);

            if (diffDays > 0) {
              return `${diffDays}d left`;
            } else if (diffHours > 0) {
              return `${diffHours}h left`;
            } else {
              const diffMinutes = Math.floor(diffMs / (1000 * 60));
              return `${diffMinutes}m left`;
            }
          } else {
            // Fallback: show plan duration label when expiresAt is missing
            console.log(
              '📌 [Sticky Duration] No expiresAt, using fallback label'
            );
            return STICKY_PLAN_LABELS[stickyPlan.id] + ' pinned';
          }
        }
      }
    }

    return null;
  };

  const formatStickyLabel = (value) => {
    if (!value) return '';
    const normalized = value.replace('pinned', '').trim();
    return `Pinned · ${normalized}`;
  };

  const showSalaryRange = (job) => {
    if (job?.salaryFrom > 0) {
      return (
        getCurrencyDisplay(job.salaryFrom) +
        ' - ' +
        getCurrencyDisplay(job.salaryTo)
      );
    }
  };
  const getCategoryLabel = (categoryValue) => {
    const category = CATEGORIES_JOB.find((cat) => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  const getTypeLabel = (typeValue) => {
    const type = TYPES_JOB.find((t) => t.value === typeValue);
    return type ? type.label : typeValue;
  };

  const getInfoChips = (job) => {
    let infoToShow = [];

    if (job?.category) {
      infoToShow.push({
        text: getCategoryLabel(job.category),
        highlight: false
      });
    }

    if (job?.type) {
      infoToShow.push({
        text: getTypeLabel(job.type),
        highlight: false
      });
    }

    if (job?.salaryFrom > 0) {
      infoToShow.push({
        text: showSalaryRange(job),
        highlight: false
      });
    }
    return infoToShow;
  };
  const history = useHistory();
  const gotToJobViewPublic = (jobId, jobTitle, companyName) => {
    history.push(buildJobUrl(jobId, jobTitle, companyName));
  };
  const getRowStyle = (job, jobToPreviewResult, idx) => {
    return isPreview && !isJobPreview(job, jobToPreviewResult)
      ? getPreviewRecordStyle(idx)
      : classes.normalRecords;
  };
  const getHighlightStyle = (jobResult, job, jobToPreviewResult) => {
    // For preview mode, use the existing logic
    if (isPreview && isJobPreview(job, jobToPreviewResult)) {
      console.log('🎬 [Preview Mode] Checking highlights for:', job?.title);
      console.log(
        '   highlightWithYellow:',
        jobToPreviewResult.highlightWithYellow
      );
      console.log(
        '   highlightWithBrandColor:',
        jobToPreviewResult.highlightWithBrandColor
      );
      console.log('   brandColor:', jobToPreviewResult.brandColor);
      console.log('   showMyLogo:', jobToPreviewResult.showMyLogo);

      let highlightClass = '';
      if (jobToPreviewResult.highlightWithYellow) {
        highlightClass = classes.highlightWithYellowShadow;
        console.log('   ✅ Applying YELLOW highlight');
      }
      if (jobToPreviewResult.highlightWithBrandColor) {
        highlightClass += ' ' + classes.highlightWithBrandColor;
        console.log('   ✅ Applying BRAND COLOR highlight');
      }
      if (jobToPreviewResult.showMyLogo) {
        console.log('   🖼️ SHOWING company logo');
      } else {
        console.log('   🚫 HIDING company logo');
      }
      return highlightClass;
    }

    // For real job data, check plan metadata
    if (!isPreview && jobResult) {
      let highlightClass = '';

      // Check if job has yellow highlight enabled in metadata
      if (hasYellowHighlight(jobResult)) {
        highlightClass = classes.highlightWithYellowShadow;
      }

      // Check if job has brand color highlight enabled in metadata
      if (hasBrandColorHighlight(jobResult)) {
        highlightClass += ' ' + classes.highlightWithBrandColor;
      }

      return highlightClass;
    }

    return '';
  };

  const buildBrandColorStyle = (brandColor) => {
    const softTint = hexToRgba(brandColor, 0.1);
    const glow = hexToRgba(brandColor, 0.35);
    const border = hexToRgba(brandColor, 0.5);

    return {
      '--brandColor': brandColor,
      borderColor: border,
      backgroundImage: `linear-gradient(90deg, ${softTint} 0%, rgba(255,255,255,0) 55%)`,
      boxShadow: `0 16px 36px -28px ${glow}`
    };
  };

  const getBrandColorStyle = (jobResult, job, jobToPreviewResult) => {
    // For preview mode, use the existing logic
    if (
      isPreview &&
      isJobPreview(job, jobToPreviewResult) &&
      jobToPreviewResult.highlightWithBrandColor
    ) {
      return buildBrandColorStyle(jobToPreviewResult.brandColor);
    }

    // For real job data, check if brand color highlight is enabled
    if (!isPreview && jobResult && hasBrandColorHighlight(jobResult)) {
      const brandColor = getBrandColorFromBenefit(jobResult);
      if (brandColor) {
        return buildBrandColorStyle(brandColor);
      }
    }

    return {};
  };
  return (
    <div className={classes.root}>
      <CardContent className={classes.content}>
        <Table className={classes.rowRecordInfoTable}>
          <TableBody>
            {jobResults.length > 0 &&
              jobResults
                .filter(({ job }) => job && job.id)
                .map((jobResult, idx) => {
                  const { job, file, company } = jobResult;
                  const isSticky = hasStickyPlan(jobResult);
                  const stickyDuration = getStickyDuration(jobResult);
                  const isGeoLocked = hasGeoLock(jobResult);

                  return (
                    <TableRow
                      key={job.id || `job-${idx}`}
                      className={getRowStyle(job, jobToPreviewResult, idx)}>
                      <TableCell>
                        <div
                          style={{
                            ...getBrandColorStyle(
                              jobResult,
                              job,
                              jobToPreviewResult
                            ),
                            cursor: isPreview ? 'default' : 'pointer'
                          }}
                          className={
                            classes.jobRow +
                            ' ' +
                            getHighlightStyle(
                              jobResult,
                              job,
                              jobToPreviewResult
                            )
                          }
                          onClick={() =>
                            !isPreview &&
                            gotToJobViewPublic(
                              job?.id,
                              job?.title,
                              company?.name
                            )
                          }>
                          <Grid
                            container
                            spacing={2}
                            wrap="nowrap"
                            alignItems="center">
                            {/* Avatar Column - Fixed width */}
                            <Grid item>
                              <div className={classes.jobAvatar}>
                                <Avatar
                                  className={classes.avatar}
                                  src={
                                    isPreview &&
                                    isJobPreview(job, jobToPreviewResult)
                                      ? // Preview mode: use showMyLogo flag
                                        jobToPreviewResult.showMyLogo
                                        ? file?.publicUrl || ''
                                        : ''
                                      : // Real mode: check plan metadata
                                      shouldShowLogo(jobResult)
                                      ? file?.publicUrl || ''
                                      : ''
                                  }>
                                  {getInitials(company?.name || 'Unknown')}
                                </Avatar>
                              </div>
                            </Grid>

                            {/* Job Details Column - Flexible width */}
                            <Grid item xs style={{ minWidth: 0 }}>
                              <div className={classes.jobDetails}>
                                <Typography
                                  className={classes.jobCompanyName}
                                  variant="h6">
                                  {company?.name || 'Unknown Company'}
                                </Typography>
                                <Typography
                                  className={classes.jobTitle}
                                  variant="h4">
                                  {job?.title || 'Untitled Job'}
                                </Typography>
                                <div className={classes.stickyChipsWrapper}>
                                  <div className={classes.hideOnMobile}>
                                    {/* Sticky indicator as primary chip */}
                                    {isSticky && stickyDuration && (
                                      <Chip
                                        className={classes.stickyChip}
                                        label={formatStickyLabel(
                                          stickyDuration
                                        )}
                                        color="default"
                                        variant="outlined"
                                        size="small"
                                      />
                                    )}
                                    {/* Geo-lock indicator */}
                                    {isGeoLocked && (
                                      <Chip
                                        className={classes.stickyChip}
                                        label="Region-based"
                                        color="default"
                                        variant="outlined"
                                        size="small"
                                      />
                                    )}
                                    <ExtraInfoChips
                                      extraInfoChips={getInfoChips(job)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </Grid>

                            {/* Time Ago Column - Fixed width */}
                            <Grid item className={classes.hideOnMobile}>
                              <div className={classes.timeAgoLabel}>
                                <Typography>
                                  {getTimeFromXDisplay(job?.createdAt)}
                                </Typography>
                              </div>
                            </Grid>
                          </Grid>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </CardContent>
    </div>
  );
};

JobTable.defaultProps = {
  jobResults: [],
  jobToPreviewResult: null,
  isPreview: false
};

JobTable.propTypes = {
  jobResults: PropTypes.array,
  jobToPreviewResult: PropTypes.object,
  isPreview: PropTypes.bool.isRequired
};

export default JobTable;
